import React, { useRef } from "react";
import { scrollToElement } from "main/javascripts/utils/ScrollUtil";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { pushUrl } from "main/javascripts/utils/UrlUtil";
import { useRouter } from "next/router";
import { addDays, addMonths, format } from "date-fns";
import { MainBlock } from "main/javascripts/components/block/MainBlock";
import { BasicBlock } from "main/javascripts/components/block/BasicBlock";
// import { nl2br } from "main/javascripts/utils/StringUtil";
import { BlockTitle } from "main/javascripts/components/molecules/Lp/BlockTitle";
// import { BlockSubTitle } from "main/javascripts/components/molecules/Lp/BlockSubTitle";
// import { Paragraph } from "main/javascripts/components/atoms/Paragraph";
import { fetchLpHotelDstRegions } from "main/javascripts/features/lp/hotelDstRegion/lpHotelDstRegionSlice";
import { Hotel } from "main/javascripts/types/hotel";
import { HotelRecommendedList } from "main/javascripts/features/lp/hotelDstRegion/components/HotelRecommendedList";
import { RegionList } from "main/javascripts/components/molecules/Lp/RegionList";
import { Country } from "main/javascripts/models/Country";
import { Breadcrumb } from "main/javascripts/components/molecules/Breadcrumb";
import { Pager } from "main/javascripts/components/pager/Pager";
import { useAppSelector, useAppDispatch } from "main/javascripts/store";
import {
  hotelDestinationRegionSelector,
  numPerPageSelector,
  pageSelector,
  totalNumSelector,
} from "../lpHotelDstRegionSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  backgroundColor,
  borderColor,
} from "main/javascripts/styles/base/colorStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { ChatEntry } from "main/javascripts/components/chat/ChatEntry";

export const LpHotelDstRegionIndexMain = ({
  regionId,
}: {
  regionId: string;
}): React.ReactElement => {
  const { t } = useTranslation(["component"]);
  const dispatch = useAppDispatch();
  const blockRef = useRef();
  const router = useRouter();

  const hotelDestinationRegion = useAppSelector(hotelDestinationRegionSelector);
  const page = useAppSelector(pageSelector);
  const totalNum = useAppSelector(totalNumSelector);
  const numPerPage = useAppSelector(numPerPageSelector);

  const breadcrumbData: any[] = [{ url: "/", label: t("label:common.home") }];
  if (hotelDestinationRegion) {
    if (
      hotelDestinationRegion.country &&
      hotelDestinationRegion.region.regionType !== "country"
    ) {
      const country: Country = hotelDestinationRegion.country;
      breadcrumbData.push({
        url: `/lp/hotels/dst_regions/${country.code}`,
        label: country.name,
      });
    }
    breadcrumbData.push({
      label: hotelDestinationRegion.region.name,
    });
  }

  const pagerHandler: (page: number) => void = async (page: number) => {
    dispatch(
      fetchLpHotelDstRegions({
        id: regionId,
        page,
      })
    );
    pushUrl({ page }, router);
    scrollToElement(blockRef.current, 100);
    // window.location.href = `${window.location.pathname}?page=${page}`;
  };

  const renderBreadcrumb = (): JSX.Element => {
    return (
      <div css={breadcrumbBlockStyle}>
        <div css={breadcrumbInnerStyle}>
          <div css={breadcrumbStyle}>
            <Breadcrumb linkData={breadcrumbData} />
          </div>
        </div>
      </div>
    );
  };

  const renderHotelList = (): JSX.Element | null => {
    if (!hotelDestinationRegion || !hotelDestinationRegion.region) {
      return null;
    }
    const region = hotelDestinationRegion.region;
    const regionName: string = hotelDestinationRegion.region.name;
    const indexParam: any = {};
    indexParam.destinationId = region.id;
    indexParam.destination = region.name;
    indexParam.destinationType = "region";
    const today: Date = new Date();
    const departureDate: Date = addMonths(today, 2);
    const dateFormat = "yyyy/LL/dd";
    indexParam.checkinDate = format(departureDate, dateFormat);
    indexParam.checkoutDate = format(addDays(departureDate, 5), dateFormat);

    const hotels: Hotel.List[] = hotelDestinationRegion.hotels;
    if (hotels && hotels.length > 0) {
      return (
        <div css={priceListBlockStyle}>
          <BasicBlock styleKey="blockWide">
            <section>
              <h2 css={titleStyle} ref={blockRef}>
                {t("lp.hotelRecommendedListTitle", { destination: regionName })}
              </h2>
              <HotelRecommendedList hotels={hotels} param={indexParam} />
              <div css={pagerBlockStyle}>
                <Pager
                  totalNum={totalNum}
                  page={page}
                  numPerPage={numPerPage}
                  handler={pagerHandler}
                />
              </div>
            </section>
          </BasicBlock>
        </div>
      );
    }
    return null;
  };

  // const renderDescription = (): JSX.Element | null => {
  //   if (!hotelDestinationRegion) {
  //     return null;
  //   }
  //   const regionName: string = hotelDestinationRegion.region.name;
  //   if (hotelDestinationRegion.region.description) {
  //     return (
  //       <>
  //         <BlockTitle>
  //           {t("lp.destinationAbout", { destination: regionName })}
  //         </BlockTitle>
  //         <Paragraph>
  //           {nl2br(hotelDestinationRegion.region.description)}
  //         </Paragraph>
  //       </>
  //     );
  //   }
  //   return null;
  // };

  // const renderBestSeason = (): JSX.Element | null => {
  //   if (!hotelDestinationRegion) {
  //     return null;
  //   }
  //   const regionName: string = hotelDestinationRegion.region.name;
  //   if (hotelDestinationRegion.region.bestSeason) {
  //     return (
  //       <section>
  //         <BlockSubTitle>
  //           {t("lp.destinationBestSeason", { destination: regionName })}
  //         </BlockSubTitle>
  //         <Paragraph>
  //           {nl2br(hotelDestinationRegion.region.bestSeason)}
  //         </Paragraph>
  //       </section>
  //     );
  //   }
  //   return null;
  // };

  // const renderComment = (): JSX.Element | null => {
  //   if (!hotelDestinationRegion) {
  //     return null;
  //   }
  //   if (hotelDestinationRegion.region.comment) {
  //     return (
  //       <section>
  //         <BlockSubTitle>{t("lp.conciergeComment")}</BlockSubTitle>
  //         <Paragraph>{nl2br(hotelDestinationRegion.region.comment)}</Paragraph>
  //       </section>
  //     );
  //   }
  //   return null;
  // };

  const renderSurroundingRegions = (): JSX.Element | null => {
    if (!hotelDestinationRegion) {
      return null;
    }
    if (hotelDestinationRegion.surroundingRegions) {
      return (
        <BasicBlock styleKey="blockWide">
          <section>
            <BlockTitle>{t("lp.surroundingRegions")}</BlockTitle>
            <RegionList
              regions={hotelDestinationRegion.surroundingRegions}
              linkUrl="/lp/hotels/dst_regions/"
            />
          </section>
        </BasicBlock>
      );
    }
    return null;
  };

  if (!hotelDestinationRegion) {
    return null;
  }
  return (
    <MainBlock>
      <div css={blockStyle}>
        {renderBreadcrumb()}
        <article>
          {renderHotelList()}
          {/*<div css={infoBlockStyle}>
            <section>
              {renderDescription()}
              {renderBestSeason()}
              {renderComment()}
            </section>
          </div>*/}
          <BasicBlock styleKey="blockWide">
            <section css={chatEntryBlockStyle}>
              <ChatEntry />
            </section>
          </BasicBlock>
          {renderSurroundingRegions()}
        </article>
      </div>
    </MainBlock>
  );
};

const blockStyle = css`
  padding: 0 0 3rem;
`;
// const infoBlockStyle = css`
//   padding: ${space.atom3x} ${space.atom} ${space.atom2x};
//   max-width: ${maxWidth.page1colWide};
//   margin: 0 auto;
//   box-sizing: border-box;
// `;
const chatEntryBlockStyle = css`
  margin: ${space.atom2x} ${space.atom} 0;
`;
const priceListBlockStyle = css`
  padding: 0 0 ${space.atom2x};
  max-width: 100%;
  box-sizing: border-box;
  background-color: ${backgroundColor.secondaryLightColor};
`;
const titleStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.headline1};
  color: ${accentColor.secondaryColor2};
  padding: ${space.atom};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.1em;
  margin: 0.5rem 0;
  position: relative;
  padding-bottom: ${space.atom2x};
  &:before {
    content: "";
    position: absolute;
    bottom: 0;
    display: block;
    height: 1px;
    width: calc(100% - ${space.atom2x});
    background: #ddd;
  }
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    display: block;
    height: 1px;
    width: 3rem;
    background: ${accentColor.primaryColor};
  }
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.headline};
  }
`;
const breadcrumbBlockStyle = css`
  padding: ${space.atom} 0 ${space.atom};
  max-width: 100%;
  box-sizing: border-box;
  background-color: ${backgroundColor.secondaryLightColor};
`;
const breadcrumbInnerStyle = css`
  max-width: ${maxWidth.page1colWide};
  margin: 0 auto;
  padding: 0 ${space.atom2x};
  box-sizing: border-box;
`;
const breadcrumbStyle = css`
  padding-bottom: ${space.atom};
  border-bottom: 1px solid ${borderColor.secondaryLightColor};
`;
const pagerBlockStyle = css`
  max-width: 30rem;
  margin: 2rem auto ${space.atom2x};
`;
